'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import { Group, Stack, Text } from '@mantine/core';

// UI local components
import UserAvatar from '@ui-kit/atoms/User';
import UserStatus from '@ui-kit/atoms/UserStatus';

// Local helpers & utils
import {
  isWorkspaceUserSuspended,
  parseUserName,
} from '@app/shared/helpers/auth';

// Types
import { User } from '@app-types/blackroom-api';
import { QueriedUser } from '@features/User/types';
import { UserTileProps } from './types';

// Style
import { palette } from '@ui-kit/colors';
import { FONT_WEIGHT } from '@ui-kit/fonts';
import useOptionalWorkspace from '@app/features/Workspace/store/hooks/useOptionalWorkspace';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function UserTile({
  user,
  avatarProps,
  containerProps,
  rootContainerProps,
  labelProps,
  onSelectUser,
  withAvatar = true,
  withLabel = true,
  withStatus = false,
  fadeLabelWhenSuspended = false,
}: UserTileProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useOptionalWorkspace();

  /* ***************************** LOCAL VARIABLES **************************** */
  const isSuspended = !!workspace && isWorkspaceUserSuspended(user, workspace);
  const { fullName } = parseUserName(user);

  /* ******************************** RENDERING ******************************* */
  return (
    <Group
      justify="space-between"
      w="100%"
      {...rootContainerProps}
      wrap="nowrap"
    >
      <Group
        gap="xs"
        {...containerProps}
        wrap="nowrap"
        align="center"
        style={{ cursor: onSelectUser ? 'pointer' : undefined }}
        onClick={() => onSelectUser?.(user as User | QueriedUser)}
      >
        {withAvatar ? (
          <UserAvatar user={user} size={34} {...avatarProps} />
        ) : null}

        <Stack align="flex-start" gap={0} flex={1} miw={0}>
          {withLabel ? (
            <Text
              c={palette.blackroomDarkGray5}
              style={{ fontWeight: FONT_WEIGHT.bold }}
              inherit
              truncate
              maw="100%"
              opacity={fadeLabelWhenSuspended && isSuspended ? 0.3 : 1}
              {...labelProps}
            >
              {fullName}
            </Text>
          ) : null}

          {withStatus ? (
            <UserStatus
              user={user}
              labelProps={{ color: palette.blackroomLightGray2 }}
            />
          ) : null}
        </Stack>
      </Group>
    </Group>
  );
}

export default UserTile;
